import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { HardDrive, Cloud, Server, CheckCircle2, XCircle, Loader2, PlugZap, Save } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

const EMPTY_CFG = { backend: 'emergent', s3_bucket: '', s3_region: '', s3_prefix: '', s3_endpoint_url: '', s3_access_key_id: '', has_secret: false };

export default function StorageSettingsPage() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [cfg, setCfg] = useState(EMPTY_CFG);
  const [canConfigure, setCanConfigure] = useState(false);
  const [secret, setSecret] = useState('');
  const [saving, setSaving] = useState(false);

  const loadStatus = async () => {
    try {
      setStatus(await api.getStorageStatus());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    loadStatus();
    (async () => {
      try {
        const c = await api.getStorageConfig();
        setCfg({ ...EMPTY_CFG, ...c });
        setCanConfigure(true);
      } catch {
        setCanConfigure(false); // non-super-admin: read-only status/test only
      }
    })();
  }, []);

  const saveConfig = async () => {
    if (cfg.backend === 's3' && !cfg.s3_bucket.trim()) {
      toast.error('S3 bucket is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        backend: cfg.backend,
        s3_bucket: cfg.s3_bucket,
        s3_region: cfg.s3_region,
        s3_prefix: cfg.s3_prefix,
        s3_endpoint_url: cfg.s3_endpoint_url,
        s3_access_key_id: cfg.s3_access_key_id,
      };
      if (secret) payload.s3_secret_access_key = secret;
      const st = await api.updateStorageConfig(payload);
      setStatus(st);
      setSecret('');
      if (secret) setCfg((p) => ({ ...p, has_secret: true }));
      toast.success('Storage settings saved — running a connection test…');
      await runTest();
    } catch (e) {
      toast.error(e.message || 'Could not save storage settings');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const r = await api.testStorageConnection();
      setResult(r);
      r.ok
        ? toast.success(`Storage OK — ${r.detail || 'write + read succeeded'}`)
        : toast.error(`Storage test failed: ${r.error || 'unknown error'}`);
    } catch (e) {
      setResult({ ok: false, error: e.message });
      toast.error(e.message);
    } finally {
      setTesting(false);
    }
  };

  const backend = status?.backend;
  const isS3 = backend === 's3';
  const setField = (k) => (e) => setCfg((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8" data-testid="storage-settings-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <HardDrive className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Object Storage</h1>
          <p className="text-sm text-muted-foreground">Where uploaded source files, logos and attachments are stored. Verify connectivity before go-live.</p>
        </div>
      </div>

      {canConfigure && (
        <Card className="p-5 space-y-4" data-testid="storage-config-card">
          <div>
            <h2 className="text-base font-semibold">Storage backend</h2>
            <p className="text-sm text-muted-foreground">Applies to uploads across all tenants. Changes take effect immediately — no redeploy.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 max-w-md">
            <button
              type="button"
              onClick={() => setCfg((p) => ({ ...p, backend: 'emergent' }))}
              className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors ${cfg.backend === 'emergent' ? 'border-primary bg-primary/10' : 'border-border/40 hover:border-border'}`}
              data-testid="storage-backend-emergent"
            >
              <Cloud className={`w-4 h-4 mt-0.5 ${cfg.backend === 'emergent' ? 'text-primary' : 'text-muted-foreground'}`} />
              <span>
                <span className="block text-xs font-medium">Emergent-managed</span>
                <span className="block text-[10px] text-muted-foreground">Hosted (default)</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setCfg((p) => ({ ...p, backend: 's3' }))}
              className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors ${cfg.backend === 's3' ? 'border-primary bg-primary/10' : 'border-border/40 hover:border-border'}`}
              data-testid="storage-backend-s3"
            >
              <Server className={`w-4 h-4 mt-0.5 ${cfg.backend === 's3' ? 'text-primary' : 'text-muted-foreground'}`} />
              <span>
                <span className="block text-xs font-medium">Amazon S3</span>
                <span className="block text-[10px] text-muted-foreground">Your own bucket</span>
              </span>
            </button>
          </div>

          {cfg.backend === 's3' && (
            <div className="space-y-3 pt-1" data-testid="storage-s3-fields">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] text-muted-foreground">Bucket name *</Label>
                  <Input value={cfg.s3_bucket} onChange={setField('s3_bucket')} placeholder="my-company-inzara" className="mt-1 text-foreground" data-testid="storage-s3-bucket" />
                </div>
                <div>
                  <Label className="text-[11px] text-muted-foreground">Region</Label>
                  <Input value={cfg.s3_region} onChange={setField('s3_region')} placeholder="us-east-1" className="mt-1 text-foreground" data-testid="storage-s3-region" />
                </div>
                <div>
                  <Label className="text-[11px] text-muted-foreground">Key prefix (optional)</Label>
                  <Input value={cfg.s3_prefix} onChange={setField('s3_prefix')} placeholder="inzara" className="mt-1 text-foreground" data-testid="storage-s3-prefix" />
                </div>
                <div>
                  <Label className="text-[11px] text-muted-foreground">Endpoint URL (optional, S3-compatible)</Label>
                  <Input value={cfg.s3_endpoint_url} onChange={setField('s3_endpoint_url')} placeholder="https://s3.us-east-1.amazonaws.com" className="mt-1 text-foreground" data-testid="storage-s3-endpoint" />
                </div>
              </div>
              <div className="rounded-lg border border-border/40 bg-muted/20 p-3 space-y-3">
                <p className="text-[11px] text-muted-foreground">
                  <strong>Recommended:</strong> leave the keys blank to use the EC2 IAM role / instance profile (no secret stored). Only fill these for an access-key pair.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Access key ID (optional)</Label>
                    <Input value={cfg.s3_access_key_id} onChange={setField('s3_access_key_id')} placeholder="AKIA…" className="mt-1 text-foreground" data-testid="storage-s3-access-key" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground">Secret access key {cfg.has_secret ? '(set — leave blank to keep)' : '(optional)'}</Label>
                    <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={cfg.has_secret ? '••••••••' : 'Secret access key'} className="mt-1 text-foreground" data-testid="storage-s3-secret" />
                  </div>
                </div>
              </div>
            </div>
          )}

          <Button onClick={saveConfig} disabled={saving} className="rounded-lg" data-testid="storage-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save storage settings
          </Button>
        </Card>
      )}

      <Card className="p-5 space-y-5" data-testid="storage-status-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
              {isS3 ? <Server className="w-5 h-5 text-primary" /> : <Cloud className="w-5 h-5 text-primary" />}
            </div>
            <div>
              <h2 className="text-base font-semibold">
                {loadingStatus ? 'Loading…' : isS3 ? 'Amazon S3 (self-hosted)' : 'Emergent-managed store'}
              </h2>
              <p className="text-sm text-muted-foreground" data-testid="storage-status-detail">
                {loadingStatus ? '' : (status?.detail || '')}
              </p>
            </div>
          </div>
          {!loadingStatus && (
            <Badge
              variant="outline"
              className={status?.ok ? 'border-emerald-500/40 text-emerald-500' : 'border-red-500/40 text-red-500'}
              data-testid="storage-config-badge"
            >
              {status?.ok ? 'Configured' : 'Not configured'}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-3 pt-1">
          <Button onClick={runTest} disabled={testing} data-testid="test-storage-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />}
            {testing ? 'Testing…' : 'Test storage connection'}
          </Button>
          <span className="text-xs text-muted-foreground">Writes a tiny probe object, reads it back, then deletes it.</span>
        </div>

        {result && (
          <div
            className={`rounded-lg border p-4 text-sm flex items-start gap-3 ${result.ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}
            data-testid="storage-test-result"
          >
            {result.ok
              ? <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              : <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
            <div className="space-y-1">
              <div className="font-medium">{result.ok ? 'Connection succeeded' : 'Connection failed'}</div>
              <div className="text-muted-foreground">
                {result.ok ? (result.detail || 'Write + read verified.') : (result.error || 'Unknown error.')}
              </div>
              <div className="text-xs text-muted-foreground">
                Backend: <strong>{result.backend || backend}</strong>
                {result.bucket ? <> · Bucket: <strong>{result.bucket}</strong></> : null}
                {result.ok && result.cleaned_up === false ? <> · (probe object could not be auto-deleted)</> : null}
              </div>
            </div>
          </div>
        )}
      </Card>

      <p className="text-xs text-muted-foreground">
        Settings here apply platform-wide at runtime. Environment variables
        (<code>STORAGE_BACKEND</code>, <code>S3_BUCKET</code>, IAM role or <code>S3_ACCESS_KEY_ID</code>/<code>S3_SECRET_ACCESS_KEY</code>)
        remain the fallback when nothing is configured here.
      </p>
    </div>
  );
}
